import { useEffect, useRef, useState } from 'react';

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  onCommitRecent: (q: string) => void;
  recent: string[];
  onClearRecent: () => void;
  onPickRecent: (q: string) => void;
  onLogoClick: () => void;
  onRandom: () => void;
}

export function Header({
  query,
  onQueryChange,
  onCommitRecent,
  recent,
  onClearRecent,
  onPickRecent,
  onLogoClick,
  onRandom,
}: Props) {
  const [focused, setFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (!searchRef.current || !dropdownRef.current) return;
      if (!searchRef.current.contains(t) && !dropdownRef.current.contains(t)) {
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showDropdown = focused && (recent.length > 0 || query.length === 0);

  return (
    <header className="ss-header">
      <div className="ss-header__inner">
        <a
          className="ss-logo"
          href="#"
          onClick={(e) => { e.preventDefault(); onLogoClick(); }}
        >
          <span className="ss-logo__mark" aria-hidden="true">🔍</span>
          <span className="ss-logo__name">
            <span className="ss-logo__title">stelsearch</span>
            <span className="ss-logo__sub">스텔라이브 영상 검색기</span>
          </span>
        </a>

        <div className="ss-search" ref={searchRef}>
          <span className="ss-search__icon" aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            type="text"
            className="ss-search__input"
            placeholder="제목, 설명, 태그로 검색…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => {
              // Ignore keys while a Hangul (or any) IME is mid-composition.
              // Without this, Enter on an unfinished syllable like 니 gets
              // double-handled: the IME commits and our handler fires,
              // producing 유니니.
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === 'Enter') {
                onCommitRecent(query);
                setFocused(false);
                (e.target as HTMLInputElement).blur();
              }
              if (e.key === 'Escape') {
                setFocused(false);
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
          {query && (
            <button
              className="ss-search__clear"
              onClick={() => onQueryChange('')}
              aria-label="지우기"
            >
              ×
            </button>
          )}

          {showDropdown && (
            <div className="ss-search__drop" ref={dropdownRef}>
              {recent.length > 0 && (
                <>
                  <div className="ss-search__drop-head">
                    <span>최근 검색</span>
                    <button
                      className="ss-search__clear-recent"
                      onClick={onClearRecent}
                    >
                      모두 지우기
                    </button>
                  </div>
                  <ul className="ss-search__recent">
                    {recent.map((r) => (
                      <li key={r}>
                        <button
                          onClick={() => {
                            onPickRecent(r);
                            setFocused(false);
                          }}
                        >
                          <span className="ss-search__recent-icon">↻</span>
                          <span>{r}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {recent.length === 0 && (
                <div className="ss-search__hint">
                  제목 · 설명 · 태그를 통합 검색합니다. 멤버 이름(한글/영문)도 인식해요.
                </div>
              )}
            </div>
          )}
        </div>

        <button className="ss-random" onClick={onRandom} title="조건에 맞는 영상 중 랜덤으로 한 편을 열어요">
          <span className="ss-random__ico">🎲</span>
          <span>랜덤 재생</span>
        </button>
      </div>
    </header>
  );
}
